// Beginner Mode full-screen shell. Renders ONLY when Beginner Mode is
// active. Hides every other surface (no nav, no footer, no FABs) so a
// brand-new visitor sees exactly one thing: the next skill to do.
//
// Four-step wizard per skill — only one pane is visible at a time so
// the eye has exactly one thing to read and one thing to do:
//   1. READ   — a 30-second plain-English explanation of the skill.
//   2. TRY    — one tap routes them into the right tool (Hub tab,
//               Planner, Library, etc.) with a starter micro-prompt.
//   3. DONE   — "I did it" marks the skill complete and advances.
//   4. REVIEW — momentum + personalized next picks (next skill, an
//               agent worth exploring, and a tool from the directory).
//
// We intentionally keep this presentation-only. The handoff to the
// existing PlaybookSearchHero does the heavy lifting of actually
// running the chat. That keeps Beginner Mode a thin, skippable layer.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  Sparkles,
  X,
  Info,
  BookOpen,
  Trophy,
  Wrench as WrenchIcon,
  Bot as BotIcon,
  Compass,
  RotateCw,
  Gauge,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import {
  useBeginnerMode,
  BEGINNER_AGENT_UNLOCK_AT,
  BEGINNER_FIRST_AGENT_PEEK_AT,
  snapStepToDifficulty,
  type WizardStep,
  type Difficulty,
} from "@/hooks/useBeginnerMode";
import ImLostPopover from "./ImLostPopover";
import SkillRecapCard from "./SkillRecapCard";
import MasteryProgressPanel from "./MasteryProgressPanel";
import BeginnerProgressHud from "./BeginnerProgressHud";
import SkillCompleteCelebration from "./SkillCompleteCelebration";
import BeginnerInlineRunner, { BUILDER_UNLOCK_AT } from "./BeginnerInlineRunner";
import WinTracker from "./WinTracker";
import { playSkillComplete, playMilestone } from "@/lib/skillSounds";
import { Bot } from "lucide-react";
import { AI_MASTERY_PATH, MASTERY_TIERS } from "@/data/aiMasteryPath";
import { useHackathon, formatUsd } from "@/hooks/useHackathon";
import { playbookForMasterySkill } from "@/data/masterySkillPlaybooks";
import { surfaceForSkill } from "@/data/masterySurfaceMap";
import { buildRecommendations } from "@/lib/recommendations";
import {
  recordBeginnerRender,
  getBeginnerRenderHistory,
} from "./beginnerDebugLog";
import { useHookCountGuard } from "./useHookCountGuard";

// Tiny static "what you'll learn" copy per tier so we don't depend on
// an LLM call to render the read step. Mirrors the tier blurbs but in
// second-person, action-framed language for an absolute beginner.
const TIER_INTRO: Record<string, string> = {
  Awareness: "You're getting your bearings — what AI actually is and how to talk to it.",
  Application: "You're putting AI to work on real tasks you do every week.",
  Leverage: "You're stacking AI into systems that multiply your output.",
  Systems: "You're wiring AI into pipelines that run without you.",
  Architecture: "You're designing AI products and businesses from scratch.",
};

// Wizard step order (display order for the stepper bar at the top).
// The step type itself lives in useBeginnerMode so it can be persisted.
//
// Stored as a frozen, module-scope constant map — one array per
// difficulty, allocated exactly ONCE at module load. Looking up
// `STEP_ORDER[d]` returns the same array instance forever, so any
// `useEffect` with `stepOrder` in its dep list only refires when
// `difficulty` actually changes (no per-render identity churn).
//
// On "hard" we collapse the wizard: the user goes Read → Done directly,
// no guided Try pane. Review still appears after Done as the recap.
const STEP_ORDER: Readonly<Record<Difficulty, readonly WizardStep[]>> =
  Object.freeze({
    easy: Object.freeze(["read", "try", "done", "review"] as const),
    medium: Object.freeze(["read", "try", "done", "review"] as const),
    hard: Object.freeze(["read", "done", "review"] as const),
  });

const STEP_META: Record<WizardStep, { label: string; icon: typeof BookOpen }> = {
  read: { label: "Learn", icon: BookOpen },
  try: { label: "Do", icon: PlayCircle },
  done: { label: "Win", icon: Check },
  review: { label: "Next", icon: Trophy },
};

const DIFFICULTY_META: Record<
  Difficulty,
  { label: string; short: string; blurb: string }
> = {
  easy: {
    label: "Easy",
    short: "E",
    blurb: "Full hand-holding, every step shown, micro-prompts visible.",
  },
  medium: {
    label: "Medium",
    short: "M",
    blurb: "Balanced — 3 key steps, standard guidance, all four panes.",
  },
  hard: {
    label: "Hard",
    short: "H",
    blurb: "Minimal scaffolding — figure it out, no guided Try pane.",
  },
};

const BeginnerShell = () => {
  const navigate = useNavigate();
  const {
    active,
    currentNum,
    completed,
    currentSkill,
    exit,
    markDone,
    goTo,
    currentStep,
    setStep,
    resume,
    difficulty,
    setDifficulty,
  } = useBeginnerMode();

  // Local alias — wizard step lives in persisted hook state so a
  // refresh drops the user back on the same pane.
  const step = currentStep;

  // Lock body scroll while the shell is open.
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  const playbook = useMemo(
    () => (currentSkill ? playbookForMasterySkill(currentSkill) : null),
    [currentSkill],
  );
  const surfaceInfo = useMemo(
    () => (currentSkill ? surfaceForSkill(currentSkill.num) : null),
    [currentSkill],
  );
  const recs = useMemo(
    () => buildRecommendations(completed),
    [completed],
  );

  // Frozen module-scope lookup — stable identity by construction.
  const stepOrder = STEP_ORDER[difficulty];
  // Always derive the rendered pane through the same snap helper used
  // by setDifficulty so render and persistence stay in sync (covers
  // legacy localStorage payloads written before atomic snapping).
  const visibleStep: WizardStep = snapStepToDifficulty(step, difficulty);
  const stepIndex = Math.max(0, stepOrder.indexOf(visibleStep));

  // Live pot for inline tease on the Win pane.
  const { pot } = useHackathon({ scoreboardLimit: 1 });
  const potLabel = formatUsd(pot?.pot_cents ?? 1042700);

  // Auto-skip the Read pane on the very first skills — the runner
  // itself is the lesson. Fires once per skill mount.
  const autoSkipRef = useRef<number | null>(null);
  useEffect(() => {
    if (!currentSkill) return;
    if (currentSkill.num <= 3 && step === "read" && autoSkipRef.current !== currentSkill.num) {
      autoSkipRef.current = currentSkill.num;
      setStep("try");
    }
  }, [currentSkill, step, setStep]);

  // Reconcile persisted step with the snapped pane.
  useEffect(() => {
    if (step !== visibleStep) setStep(visibleStep);
  }, [step, visibleStep, setStep]);

  // First skill the user hasn't marked done. Powers the "Resume" chip.
  const firstIncompleteNum = useMemo(() => {
    const done = new Set(completed);
    return (
      AI_MASTERY_PATH.find((s) => !done.has(s.num))?.num ??
      AI_MASTERY_PATH.length
    );
  }, [completed]);

  // "Your step changed" banner: shown when a difficulty switch hides
  // the previously-saved pane (e.g. Try → Hard snaps you to Done).
  const [snapNotice, setSnapNotice] = useState<{
    from: WizardStep;
    to: WizardStep;
    difficulty: Difficulty;
  } | null>(null);
  const prevRef = useRef({ difficulty, step });
  useEffect(() => {
    const prev = prevRef.current;
    if (prev.difficulty !== difficulty) {
      const hiddenNow = !STEP_ORDER[difficulty].includes(prev.step);
      setSnapNotice(
        hiddenNow && prev.step !== visibleStep
          ? { from: prev.step, to: visibleStep, difficulty }
          : null,
      );
    }
    prevRef.current = { difficulty, step };
  }, [difficulty, step, visibleStep]);

  // Skill heading ref — focused & scrolled into view after a Resume
  // click so screen readers announce the new skill and the user's
  // viewport lands exactly on it (header is sticky and would otherwise
  // hide the title on shorter screens).
  const skillHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const pendingFocusRef = useRef(false);
  useEffect(() => {
    if (!pendingFocusRef.current) return;
    pendingFocusRef.current = false;
    const el = skillHeadingRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.focus({ preventScroll: true });
  }, [currentNum]);

  // Resume is a localStorage write so it's effectively instant, but
  // we still surface a brief busy state + an error fallback so the
  // chip never feels dead and any surprise (quota, private mode) is
  // recoverable. Status auto-clears once the new skill renders.
  const [resumeStatus, setResumeStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");

  // Celebration trigger — increments each time the user marks a skill
  // done so the confetti + HUD XP bump fire exactly once per win.
  const [celebrateKey, setCelebrateKey] = useState(0);

  const handleResume = () => {
    setResumeStatus("loading");
    pendingFocusRef.current = true;
    try {
      resume();
      // Clear shortly after the next paint so the spinner is visible
      // even on instant writes (avoids a flicker-free "did anything
      // happen?" feeling).
      window.setTimeout(() => setResumeStatus("idle"), 250);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BeginnerShell] resume failed", err);
      pendingFocusRef.current = false;
      setResumeStatus("error");
    }
  };

  // DEV-only render snapshot — written into a module-scope ring buffer
  // so the error boundary can read the last few renders when it
  // catches a crash. Edge-trigger to console.table on first blank.
  const wasBlankRef = useRef(false);
  if (import.meta.env.DEV) {
    const willEarlyReturn = !active || !currentSkill;
    recordBeginnerRender({
      t: new Date().toISOString().slice(11, 23),
      branch: willEarlyReturn ? "EARLY_RETURN_NULL" : "RENDER_SHELL",
      active,
      hasCurrentSkill: !!currentSkill,
      currentNum,
      step,
      visibleStep,
      difficulty,
      completedCount: completed.length,
    });
    if (willEarlyReturn && !wasBlankRef.current) {
      // eslint-disable-next-line no-console
      console.warn("[BeginnerShell] blank screen — render history:");
      // eslint-disable-next-line no-console
      console.table(getBeginnerRenderHistory());
    }
    wasBlankRef.current = willEarlyReturn;
  }

  // DEV-only invariant: once we've seen a real `currentSkill`, it must
  // never silently flip back to undefined. That transition would force
  // the shell to early-return null mid-session (blank screen) — almost
  // always a sign that persisted state drifted out of the valid skill
  // range or that the mastery path got mutated. Throwing here surfaces
  // the bug at the exact render it happens, and the ErrorBoundary's
  // render-history snapshot pinpoints the offending state.
  const sawSkillRef = useRef(false);
  if (import.meta.env.DEV) {
    if (currentSkill) {
      sawSkillRef.current = true;
    } else if (sawSkillRef.current && active) {
      throw new Error(
        `[BeginnerShell] invariant violated: currentSkill became undefined after being defined ` +
          `(currentNum=${currentNum}, step=${step}, difficulty=${difficulty}). ` +
          `Persisted beginner state likely points to a skill number outside AI_MASTERY_PATH.`,
      );
    }
  }

  // Last hook before the early return — warns in DEV if the number of
  // hooks above this line ever shifts between renders.
  useHookCountGuard("BeginnerShell");

  // Single early-return AFTER every hook above. Never add hooks below.
  if (!active || !currentSkill) return null;

  const tierMeta = MASTERY_TIERS[currentSkill.tier];
  const totalDone = completed.length;
  const progressPct = Math.round((totalDone / AI_MASTERY_PATH.length) * 100);
  const isAlreadyDone = completed.includes(currentNum);
  // Hide advanced controls (difficulty, resume, agent countdown,
  // mastery panel, stepper, snap notice) until the user has banked a
  // few wins. Beginners just see Skill X/100 + a thin progress bar.
  const showAdvanced = totalDone >= 5;
  // Strip the playbook step list / hint box for very early skills —
  // the runner is the lesson at that stage.
  const stripRead = currentSkill.num <= 10;
  // Two-stage countdown: aim at the early peek (#10) first, then once
  // that's done aim at the deeper Application-tier unlock (#25).
  // Hides itself entirely once both are reached.
  const peekDone = completed.includes(BEGINNER_FIRST_AGENT_PEEK_AT);
  const nextAgentMilestone = peekDone
    ? BEGINNER_AGENT_UNLOCK_AT
    : BEGINNER_FIRST_AGENT_PEEK_AT;
  const stepsToAgent = Math.max(0, nextAgentMilestone - totalDone);
  // Only surface "Resume" when the user is parked on a skill they've
  // already completed AND there's an earlier incomplete skill to jump
  // back to. Avoids showing the chip when they're simply ahead.
  const canResume =
    isAlreadyDone && firstIncompleteNum < currentNum;

  const handleTry = () => {
    // Per-skill mapped surface — drops the user exactly where this
    // skill should be practiced (Hub tab, Planner, Library, etc.).
    // Default to the home runner if no mapping exists.
    const target =
      surfaceInfo?.surface ??
      `/?skill=${encodeURIComponent(currentSkill.label)}`;
    // Advance to "Done" before navigating so when the user comes back
    // they land on the confirmation pane, not the read pane again.
    setStep("done");
    navigate(target);
  };

  const handleMarkDone = () => {
    if (!isAlreadyDone) {
      markDone();
      setCelebrateKey((k) => k + 1);
      // Sound: louder fanfare every 5th skill, plain jingle otherwise.
      const newTotal = totalDone + 1;
      if (newTotal > 0 && newTotal % 5 === 0) playMilestone();
      else playSkillComplete();
    }
    setStep("review");
  };

  const handleContinue = () => {
    // Advances to the next skill; the useEffect on currentNum will
    // reset us back to step "read".
    goTo(currentNum + 1);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-background overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Beginner Mode — guided AI mastery"
    >
      <SkillCompleteCelebration triggerKey={celebrateKey} />
      {/* Top bar: progress + exit */}
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <span className="text-xs font-mono font-bold text-foreground truncate">
              Guided mode
            </span>
            <span className="text-[10px] font-mono text-muted-foreground hidden sm:inline">
              · {totalDone}/{AI_MASTERY_PATH.length} done ({progressPct}%)
            </span>
          </div>
          <div className="flex-1 hidden sm:block">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={exit}
            className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-border bg-card/40 px-2.5 py-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
            aria-label="Exit guided mode"
          >
            <X className="w-3.5 h-3.5" />
            Exit
          </button>
        </div>
      </header>

      {/* Persistent gamified HUD — score + prize, always in view */}
      <BeginnerProgressHud
        currentNum={currentNum}
        completedCount={totalDone}
        xpBumpKey={celebrateKey}
      />

      {/* Main card */}
      <main className="container mx-auto px-4 py-8 sm:py-12 max-w-2xl">
        {/* Skill number — single tiny chip, no tier jargon */}
        <div className="mb-4">
          <span className="text-[10px] font-mono text-muted-foreground">
            Skill #{currentSkill.num} of {AI_MASTERY_PATH.length}
          </span>
        </div>

        {/* The skill */}
        <h1
          ref={skillHeadingRef}
          tabIndex={-1}
          className="font-mono font-extrabold text-2xl sm:text-3xl text-foreground mb-3 outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
        >
          {currentSkill.label}
        </h1>

        {/* LEARN — one sentence. */}
        <p className="text-base font-mono text-foreground/90 leading-relaxed mb-6">
          {playbook?.tagline ?? TIER_INTRO[currentSkill.tier]}
        </p>

        {/* DO — inline runner. The Run button lives inside it. */}
        {surfaceInfo?.microPrompt ? (
          <div className="mb-6">
            <BeginnerInlineRunner
              key={currentSkill.num}
              skillNum={currentSkill.num}
              skillLabel={currentSkill.label}
              starterPrompt={surfaceInfo.microPrompt}
              onRan={() => {
                // Auto-bank the win on first successful run.
                if (!isAlreadyDone) {
                  markDone();
                  setCelebrateKey((k) => k + 1);
                  const newTotal = totalDone + 1;
                  if (newTotal > 0 && newTotal % 5 === 0) playMilestone();
                  else playSkillComplete();
                }
                setStep("review");
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleMarkDone}
            className="mb-6 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Check className="w-4 h-4" />
            I did it
            <ArrowRight className="w-4 h-4" />
          </button>
        )}

        {/* WIN — only the Next button. Appears once the skill is banked. */}
        {(isAlreadyDone || visibleStep === "review") && (
          <WinTracker
            totalDone={totalDone}
            xpAmount={50}
            potLabel={potLabel}
            bumpKey={celebrateKey}
          />
        )}
        {(isAlreadyDone || visibleStep === "review") && (
          <button
            type="button"
            onClick={handleContinue}
            disabled={currentNum >= AI_MASTERY_PATH.length}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {currentNum >= AI_MASTERY_PATH.length ? (
              <>🎉 You finished the path!</>
            ) : (
              <>
                Next skill (#{currentNum + 1})
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </main>
      {/* Recap interstitial — mounts above main when a skill was just
          marked done but the user hasn't continued yet. */}
      <SkillRecapCard />
    </div>
  );
};

export default BeginnerShell;
